import { action, atom } from '@reatom/framework'

import { TradeApiClient as MainApiClient } from '@blade/api-client'
import { PathfinderApiClient } from '@blade/pathfinder-api-client'
import { environmentAtom, type Environment } from '@root/shared'
import { createCustomFetch } from '@root/shared/api/lib/custom-fetch'
import {
  createPathfinderWsClient,
  deriveDefaultPathfinderWsUrl,
  type PathfinderWsClient,
} from '@root/shared/api/pathfinder/pathfinder-ws-client'
import { createDisabledPathfinderWsClient } from '@root/shared/api/pathfinder/pathfinder-ws-disabled'
import {
  initializeTradeApiClient,
  tradeApiClientAtom,
} from '@root/shared/api/trade-api-client-atom'
import { TgUtil } from '@root/shared/lib/telegram'

import { reatomChartWebsocket } from '../realtime/chart-websocket'
import {
  reatomWebsocketMain,
  type ReatomWebsocketMain,
} from '../realtime/reatom-websocket-main'
import { TauriSecureStorageService } from '../secure-storage/tauri-secure-storage'

import type { ChartWebsocketReatom } from '../realtime/chart-websocket'
import type { TradeApiClient } from '@blade/trade-api-client'
import type { SecureStorageService } from '@root/features/secure-storage/secure-storage-service'

export interface Dependencies {
  chartWebsocket: ChartWebsocketReatom
  mainWebsocket: ReatomWebsocketMain
  environment: Environment
  tradeApiClient: TradeApiClient<unknown>
  mainApiClient: MainApiClient<unknown>
  pathfinderApiClient: PathfinderApiClient<unknown>
  secureStorageService: SecureStorageService
  pathfinderWsClient: PathfinderWsClient
}

/**
 * We use this atom as means to provide dependencies, so it's fine to cast to any here
 */
export const dependenciesAtom = atom<Dependencies>(
  {} as Dependencies,
  'dependenciesAtom',
)

export const supportAccountLinkAtom = atom((ctx) => {
  const supportAccount = ctx.spy(dependenciesAtom).environment.supportAccount
  return TgUtil.createTelegramLink(supportAccount)
}, 'supportAccountLinkAtom')

export const initializeDependencies = action((ctx) => {
  initializeTradeApiClient(ctx)
  // eslint-disable-next-line @reatom/unit-naming-rule
  const chartWebsocket = reatomChartWebsocket()
  // eslint-disable-next-line @reatom/unit-naming-rule
  const mainWebsocket = reatomWebsocketMain()
  const environment = ctx.get(environmentAtom)

  const { tradeApiClient } = ctx.get(tradeApiClientAtom)
  const mainApiClient = new MainApiClient({
    baseUrl: environment.apiHost,
    baseApiParams: { credentials: 'omit' },
  })
  const pathfinderApiClient = new PathfinderApiClient({
    baseUrl: environment.pathfinderApiHost,
    baseApiParams: { credentials: 'omit' },
    // TODO: Remove once BE will have cors headers
    customFetch: createCustomFetch({
      withDeviceId: false,
      withPlatform: false,
      proxySubPath: 'pathfinder-proxy',
    }),
  })

  // An environment without a pathfinder gets a client that says so. The real
  // one throws when it is built without a URL, and this runs while the
  // application starts — so a missing pathfinder stopped the terminal at its
  // splash screen with an uncaught error.
  const pathfinderWsUrl = deriveDefaultPathfinderWsUrl(
    environment.pathfinderApiHost,
  )
  const pathfinderWsClient = !pathfinderWsUrl
    ? createDisabledPathfinderWsClient()
    : createPathfinderWsClient({
        wsUrl: pathfinderWsUrl,
        // HTTP-fallback uses the same host's `/v1/quote` endpoint.
        // httpFallbackUrl: environment.pathfinderApiHost,
        // disable http fallback
        httpFallbackUrl: undefined,
        // While server-side `ws_transaction_refresh_interval_ms` is null on staging,
        // we self-refresh every 45s. Set to 0 once server-side will be set.
        autoRefreshMs: 45_000,
        httpFallbackDelayMs: 1000,
      })

  dependenciesAtom(ctx, {
    environment,
    tradeApiClient,
    pathfinderApiClient,
    pathfinderWsClient,
    mainApiClient,
    chartWebsocket,
    mainWebsocket,
    secureStorageService: TauriSecureStorageService,
  })
}, 'initializeDependencies')

type Logger = Pick<Console, 'log' | 'warn' | 'error' | 'info'>
export const loggerAtom = atom<Logger>(console, 'loggerAtom')
